import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, Bell, Gauge, Users, Save, Power, ChevronRight, Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import Drawer from "@/components/Drawer";
import { LoadingState } from "@/components/LoadingState";
import { dashboardApi, orgApi } from "@/api/endpoints";
import type { OrgDashboard, Organisation } from "@/types";

export default function StaffOrgDetailPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [org, setOrg] = useState<Organisation | null>(null);
  const [dashboard, setDashboard] = useState<OrgDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const editForm = useForm<Partial<Organisation>>();

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [o, d] = await Promise.all([orgApi.get(orgId), dashboardApi.org(orgId).catch(() => null)]);
      setOrg(o);
      setDashboard(d);
      setNotes(o.internal_notes ?? "");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [orgId]);

  const toggleActive = async () => {
    if (!org) return;
    const next = !org.is_active;
    if (!window.confirm(next ? `Reactivate ${org.name}?` : `Suspend ${org.name}? Its users won't be able to sign in.`)) return;
    try {
      const updated = await orgApi.update(org.id, { is_active: next });
      setOrg(updated);
      toast.success(next ? "Organisation reactivated" : "Organisation suspended");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };

  const saveNotes = async () => {
    if (!org) return;
    setSavingNotes(true);
    try {
      const updated = await orgApi.update(org.id, { internal_notes: notes });
      setOrg(updated);
      toast.success("Internal notes saved");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    } finally {
      setSavingNotes(false);
    }
  };

  const openEdit = () => {
    if (!org) return;
    editForm.reset({
      name: org.name,
      industry_type: org.industry_type ?? "",
      website: org.website ?? "",
      vat_number: org.vat_number ?? "",
      business_registration_number: org.business_registration_number ?? "",
      billing_contact_name: org.billing_contact_name ?? "",
      billing_contact_email: org.billing_contact_email ?? "",
      billing_contact_phone: org.billing_contact_phone ?? "",
      billing_address: org.billing_address ?? "",
    });
    setEditOpen(true);
  };

  const submitEdit = editForm.handleSubmit(async (values) => {
    if (!org) return;
    try {
      const updated = await orgApi.update(org.id, values);
      setOrg(updated);
      toast.success("Organisation updated");
      setEditOpen(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  });

  if (loading || !org) {
    return <AppLayout><LoadingState /></AppLayout>;
  }

  const sections = [
    { to: `/staff/organisations/${orgId}/buildings`, label: "Buildings", icon: <Building2 size={18} />, hint: `${dashboard?.building_count ?? 0} buildings` },
    { to: `/staff/organisations/${orgId}/users`, label: "Users", icon: <Users size={18} />, hint: "Invite and manage" },
    { to: `/staff/organisations/${orgId}/readings`, label: "Readings", icon: <Gauge size={18} />, hint: "Submit on behalf" },
    { to: `/staff/organisations/${orgId}/alerts`, label: "Alerts", icon: <Bell size={18} />, hint: `${dashboard?.unresolved_alert_count ?? 0} open` },
  ];

  return (
    <AppLayout>
      <PageHeader
        title={org.name}
        subtitle={org.is_active ? "Active organisation" : "Suspended — users cannot sign in"}
        actions={
          <div className="flex gap-2">
            <button onClick={openEdit} className="btn-secondary" type="button">
              <Pencil size={16} /> Edit
            </button>
            <button onClick={toggleActive} className={org.is_active ? "btn-danger" : "btn-primary"} type="button">
              <Power size={16} /> {org.is_active ? "Suspend" : "Reactivate"}
            </button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Buildings" value={dashboard?.building_count ?? 0} icon={<Building2 size={18} />} />
        <KpiCard label="Units" value={dashboard?.unit_count ?? 0} icon={<Users size={18} />} accent="blue" />
        <KpiCard label="Meters" value={dashboard?.meter_count ?? 0} icon={<Gauge size={18} />} accent="slate" />
        <KpiCard
          label="Open alerts"
          value={dashboard?.unresolved_alert_count ?? 0}
          icon={<Bell size={18} />}
          accent={(dashboard?.unresolved_alert_count ?? 0) > 0 ? "red" : "brand"}
        />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {sections.map((s) => (
          <Link key={s.to} to={s.to} className="card p-4 hover:border-brand-500 transition flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-brand-700 mb-1">{s.icon}<span className="font-medium">{s.label}</span></div>
              <p className="text-xs text-slate-500">{s.hint}</p>
            </div>
            <ChevronRight size={18} className="text-slate-400" />
          </Link>
        ))}
      </div>

      {/* Internal notes */}
      <div className="card p-5 mb-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-2">Internal notes (staff only)</h2>
        <p className="text-xs text-slate-500 mb-3">
          Visible only to UmdoniWay staff. Use for context: contact info, billing notes, support history.
        </p>
        <textarea
          rows={5}
          className="input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Primary contact: Mary Smith +27 82 555 1234; pays R5k/mo; migrated from competitor Q1 2026..."
        />
        <div className="flex justify-end mt-3">
          <button onClick={saveNotes} disabled={savingNotes} className="btn-primary" type="button">
            <Save size={16} /> {savingNotes ? "Saving…" : "Save notes"}
          </button>
        </div>
      </div>

      {/* Org details */}
      <div className="card p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Organisation details</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs text-slate-500">Industry</dt>
            <dd className="text-slate-700">{org.industry_type ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Website</dt>
            <dd className="text-slate-700">{org.website ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Billing contact</dt>
            <dd className="text-slate-700">{org.billing_contact_name ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Billing email</dt>
            <dd className="text-slate-700">{org.billing_contact_email ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Billing phone</dt>
            <dd className="text-slate-700">{org.billing_contact_phone ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">VAT number</dt>
            <dd className="text-slate-700">{org.vat_number ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500">Registration #</dt>
            <dd className="text-slate-700">{org.business_registration_number ?? "—"}</dd>
          </div>
          <div className="md:col-span-2">
            <dt className="text-xs text-slate-500">Billing address</dt>
            <dd className="text-slate-700 whitespace-pre-line">{org.billing_address ?? "—"}</dd>
          </div>
        </dl>
      </div>

      <Drawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title="Edit organisation"
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" type="button" onClick={() => setEditOpen(false)}>Cancel</button>
            <button className="btn-primary" type="button" onClick={submitEdit}>Save</button>
          </div>
        }
      >
        <form className="space-y-3" onSubmit={submitEdit}>
          <div>
            <label className="label">Organisation name</label>
            <input className="input" {...editForm.register("name", { required: true })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Industry</label>
              <input className="input" {...editForm.register("industry_type")} />
            </div>
            <div>
              <label className="label">Website</label>
              <input className="input" {...editForm.register("website")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">VAT number</label>
              <input className="input" {...editForm.register("vat_number")} />
            </div>
            <div>
              <label className="label">Registration #</label>
              <input className="input" {...editForm.register("business_registration_number")} />
            </div>
          </div>
          <hr className="my-2 border-slate-200" />
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Billing</p>
          <div>
            <label className="label">Billing contact name</label>
            <input className="input" {...editForm.register("billing_contact_name")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Billing email</label>
              <input type="email" className="input" {...editForm.register("billing_contact_email")} />
            </div>
            <div>
              <label className="label">Billing phone</label>
              <input className="input" {...editForm.register("billing_contact_phone")} />
            </div>
          </div>
          <div>
            <label className="label">Billing address</label>
            <textarea rows={3} className="input" {...editForm.register("billing_address")} />
          </div>
        </form>
      </Drawer>
    </AppLayout>
  );
}
